import isNil from 'lodash/isNil';
import { useEffect, useMemo, useState } from 'react';
import { useInfiniteQuery, useQuery, useQueryClient } from 'react-query';
import { toastr } from 'react-redux-toastr';
import useCustomEvent from '../../hooks/useCustomEvent';
import { useAppFlags } from '../../utils/featureFlags';
import useSelectedProperty from '../../hooks/useSelectedProperty';
import { useSendMessage } from '../../hooks/useSendMessage';
import { getTextingPhoneNumber } from '../../utils/property-helpers';
import { NEW_CONV_SUFFIX } from './constants';
import {
  CONVERSATIONS_CHANGED_EVENT,
  CONVERSATION_STATUS_CHANGED_EVENT,
} from './customEvents';
import {
  fetchConversations,
  fetchCommunicationPreferences,
  fetchHouseholdMembers,
  fetchLeslieConfig,
  fetchMessages,
  resolveMessageNeedsHelp,
  sendMessage,
  updateConversation,
  updateLeslieConfig,
} from './data';
import intlMessages from './messages';
import { Props } from './types';

export function useTextingTab({ prospectId, householdId, intl }: Props) {
  const selectedProperty = useSelectedProperty();
  const [selectedConversationId, setSelectedConversationId] = useState();
  const { leslie: leslieEnabled } = useAppFlags();
  const {
    conversations,
    messages,
    isLoading,
    refreshMessages,
    refreshConversations,
    loadMoreMessages,
    hasMoreMessages,
    infoOptedIn,
    refreshCommunicationPreferences,
  } = useTabData({
    prospectId,
    householdId,
    intl,
    selectedConversationId,
    setSelectedConversationId,
    leslieEnabled,
  });
  const textingPhoneNumber = useMemo(
    () => getTextingPhoneNumber(selectedProperty),
    [selectedProperty],
  );
  const [message, setMessage] = useState('');
  // Which Leslie-flagged message the next send answers, or null for an ordinary send. Held as
  // the message rather than its id because the component previews it above the composer, and
  // held here rather than in the library because it has to survive the send: it names the flag
  // to clear once the reply has actually gone out.
  const [replyingToMessage, setReplyingToMessage] = useState(null);
  const { emit: emitConversationsChanged } = useCustomEvent({
    eventKey: CONVERSATIONS_CHANGED_EVENT,
  });
  const { emit: emitConversationStatusChanged } = useCustomEvent({
    eventKey: CONVERSATION_STATUS_CHANGED_EVENT,
  });
  const { textingCharacterLimitIncrease } = useAppFlags();
  const { leslie, isUpdatingLeslie, handleToggleLeslie } = useLeslieStatus({
    prospectId,
    householdId,
    intl,
  });

  const recipientCategory = useMemo(() => {
    const url = window.location.href;
    if (url.includes('prospect')) return 'prospects';
    if (url.includes('application')) return 'applicants';
    if (url.includes('prior-resident')) return 'priorResidents';
    if (url.includes('resident')) return 'residents';
  }, []);

  useEffect(() => {
    emitConversationsChanged(conversations);
  }, [conversations, emitConversationsChanged]);

  function handleLoadMoreMessages() {
    loadMoreMessages();
  }

  function handleSelectConversation(conversationId) {
    setSelectedConversationId(conversationId);
    // The reply names a message in the conversation being left, so carrying it across would
    // point the next send at a flag on a different leaseholder.
    setReplyingToMessage(null);
    // And the draft goes with it. A half-typed message is addressed to the person it was being
    // written to; carrying it into the next conversation risks sending one recipient's text to
    // another, which is a disclosure, not a convenience.
    setMessage('');

    const conv = conversations.find((c) => c.id === conversationId);

    if (conv.status !== 'new-message' || conv.id.includes(NEW_CONV_SUFFIX))
      return;

    updateConversation(conversationId, {
      conversationStatus: 'UNRESOLVED',
      propertyId: selectedProperty.id,
    }).then(() => {
      refreshConversations();
      emitConversationStatusChanged({
        conversationStatus: 'UNRESOLVED',
        recipientCategory,
      });
    });
  }

  function handleMessageChange(value) {
    setMessage(value);
  }

  const { isSending, handleSendMessage } = useSendMessage({
    refreshCommunicationPreferences,
    intl,
    messages: {
      errorHeader: intlMessages.errorHeader,
      userNoLongerOptedInErrorMsg: intlMessages.userNoLongerOptedInErrorMsg,
      failedToSendMessageError: intlMessages.failedToSendMessageErrorMsg,
    },
    onSend: async (messageText: string) => {
      if (!conversations || !Array.isArray(conversations)) {
        throw new Error('Conversations not loaded');
      }

      const conversation = conversations.find(
        (c) => c.id === selectedConversationId,
      );

      if (!conversation) {
        throw new Error('Selected conversation not found');
      }

      const createdMessage = await sendMessage({
        message: messageText,
        conversation,
      });

      // Only a send made from the reply state resolves anything. An ordinary response leaves
      // every flag exactly where it was, which is what keeps a routine reply from silently
      // closing a help request nobody answered.
      if (replyingToMessage) {
        try {
          await resolveMessageNeedsHelp({ messageId: replyingToMessage.id });
          // Two-way recomputes the conversation's own status from the flags that remain, so
          // the household indicator follows from the refetch below rather than being cleared
          // here.
          emitConversationStatusChanged({
            conversationStatus: 'NEEDS_HELP_RESOLVED',
            recipientCategory,
          });
        } catch (error) {
          // Caught here rather than left to useSendMessage's handler, which would report that
          // the message failed to send. It did send — the prospect has it — and only the flag
          // is still up, so the refetch below leaves it flagged and the user can reply again.
          toastr.error(
            intl.formatMessage(intlMessages.errorHeader),
            intl.formatMessage(intlMessages.failedToResolveLeslieHelpErrorMsg),
          );
        }
      }

      await refreshConversations();
      await refreshMessages();

      setMessage('');
      setReplyingToMessage(null);
      // A first message to a recipient creates the conversation, so the id only arrives on the
      // response. Guarded because an absent one would deselect the conversation entirely and
      // blank the thread the user is looking at.
      if (createdMessage?.conversationId) {
        setSelectedConversationId(createdMessage.conversationId);
      }
    },
  });

  function handleToggleResolved() {
    const conv = conversations.find((c) => c.id === selectedConversationId);
    const isResolved = conv.status === 'resolved';
    const conversationStatus = isResolved ? 'UNRESOLVED' : 'RESOLVED';

    updateConversation(selectedConversationId, {
      conversationStatus,
      propertyId: selectedProperty.id,
    }).then(() => {
      refreshConversations();
      emitConversationStatusChanged({ conversationStatus, recipientCategory });
    });
  }

  function handleReplyToMessage(repliedMessage) {
    setReplyingToMessage(repliedMessage);
  }

  // Leaves the flag alone by design: cancelling backs out of the reply, it does not answer it.
  function handleCancelReply() {
    setReplyingToMessage(null);
  }

  return {
    conversations,
    messages,
    hasMoreMessages,
    selectedConversationId,
    message,
    isSending,
    textingPhoneNumber,
    isLoading,
    infoOptedIn,
    messageMaxLength: textingCharacterLimitIncrease ? 1600 : 160,
    handleLoadMoreMessages,
    handleSelectConversation,
    handleMessageChange,
    handleSendMessage,
    handleToggleResolved,
    leslie: leslie ? { ...leslie, isUpdating: isUpdatingLeslie } : undefined,
    handleToggleLeslie,
    // All three withheld together when the flag is off, so the library's reply union collapses
    // to its no-op branch and none of this workflow is reachable.
    replyingToMessage: leslieEnabled
      ? replyingToMessage ?? undefined
      : undefined,
    handleReplyToMessage: leslieEnabled ? handleReplyToMessage : undefined,
    handleCancelReply: leslieEnabled ? handleCancelReply : undefined,
  };
}

/**
 * Household-level Leslie on/off state for the Texting tab.
 *
 * Returns `leslie: undefined` for every case where the control must not be shown — the
 * `leslie` feature flag being off, Leslie not being enabled for the property, or two-way
 * texting not being active. The last two arrive together on the config response as
 * `available`, so this costs one request rather than three.
 */
export function useLeslieStatus({ prospectId, householdId, intl }) {
  const selectedProperty = useSelectedProperty();
  const { leslie: leslieFlagEnabled } = useAppFlags();

  // The request is scoped to /{organizationId}/{propertyId}/..., so both belong in the cache
  // key: without them a switch of selected property could be served the previous property's
  // config. `enabled` waits for the property too, since the URL cannot be built without it.
  const { data: leslieConfig, refetch: refreshLeslieConfig } = useQuery(
    [
      'leslieConfig',
      selectedProperty?.organizationId,
      selectedProperty?.id,
      prospectId,
      householdId,
    ],
    () => fetchLeslieConfig({ prospectId, householdId, selectedProperty }),
    {
      enabled: Boolean(
        leslieFlagEnabled &&
          selectedProperty?.id &&
          (prospectId || householdId),
      ),
      // Reading, not writing — the update message would claim a change was attempted.
      onError: () => {
        toastr.error(
          intl.formatMessage(intlMessages.errorHeader),
          intl.formatMessage(intlMessages.failedToLoadLeslieErrorMsg),
        );
      },
    },
  );

  const [isUpdatingLeslie, setIsUpdatingLeslie] = useState(false);

  const leslie = useMemo(() => {
    if (!leslieFlagEnabled || !leslieConfig || !leslieConfig.available) {
      return undefined;
    }
    return { enabled: leslieConfig.enabled };
  }, [leslieFlagEnabled, leslieConfig]);

  async function handleToggleLeslie(nextEnabled) {
    setIsUpdatingLeslie(true);
    try {
      await updateLeslieConfig({
        prospectId,
        householdId,
        selectedProperty,
        enabled: nextEnabled,
      });
      await refreshLeslieConfig();
    } catch (error) {
      toastr.error(
        intl.formatMessage(intlMessages.errorHeader),
        intl.formatMessage(intlMessages.failedToUpdateLeslieErrorMsg),
      );
    } finally {
      setIsUpdatingLeslie(false);
    }
  }

  return { leslie, isUpdatingLeslie, handleToggleLeslie };
}

function useTabData({
  prospectId,
  householdId,
  intl,
  selectedConversationId,
  setSelectedConversationId,
  leslieEnabled,
}) {
  const { smsDoubleOptIn } = useAppFlags();
  const { householdMembers, isLoading: isHouseholdMembersLoading } =
    useHouseholdMembers({ prospectId, householdId, intl });

  const {
    conversations,
    isLoading: isConversationsLoading,
    refreshConversations,
  } = useConversations({
    intl,
    selectedConversationId,
    setSelectedConversationId,
    householdMembers,
    leslieEnabled,
  });

  const selectedConversation = useMemo(
    () =>
      conversations &&
      conversations.find((c) => c.id === selectedConversationId),
    [conversations, selectedConversationId],
  );

  const contactId = useMemo(() => {
    if (!selectedConversation || !householdMembers) return null;
    const recipientId = selectedConversation.recipients?.[0]?.id;
    const householdMember = householdMembers.find(
      (hm) => hm.id === recipientId,
    );
    return householdMember?.contactId;
  }, [selectedConversation, householdMembers]);

  const { messages, refreshMessages, loadMoreMessages, hasMoreMessages } =
    useMessages({
      selectedConversation,
      intl,
      leslieEnabled,
    });

  const isLoading = isConversationsLoading || isHouseholdMembersLoading;

  const { infoOptedIn, refreshCommunicationPreferences } =
    useCommunicationPreferences({
      contactId,
      intl,
    });

  return {
    conversations,
    messages,
    isLoading,
    refreshMessages,
    refreshConversations,
    loadMoreMessages,
    hasMoreMessages,
    // When smsDoubleOptIn is disabled, default to treating the contact as opted in
    // to preserve the legacy behavior (no double opt-in required).
    infoOptedIn: smsDoubleOptIn ? infoOptedIn : true,
    refreshCommunicationPreferences,
  };
}

function useHouseholdMembers({ prospectId, householdId, intl }) {
  const selectedProperty = useSelectedProperty();
  const queryClient = useQueryClient();
  const { isLoading, data: householdMembers } = useQuery(
    ['householdMembers', prospectId, householdId],
    () => fetchHouseholdMembers({ prospectId, householdId, selectedProperty }),
    {
      onError: (error) => {
        toastr.error(
          intl.formatMessage(intlMessages.errorHeader),
          intl.formatMessage(intlMessages.failedToLoadHouseholdMembersErrorMsg),
        );
      },
    },
  );

  useEffect(() => {
    return () => {
      queryClient.removeQueries(['householdMembers', prospectId, householdId]);
    };
  }, [householdId, prospectId, queryClient]);

  return {
    householdMembers,
    isLoading,
  };
}

function useConversations({
  intl,
  selectedConversationId,
  setSelectedConversationId,
  householdMembers,
  leslieEnabled,
}) {
  const {
    data: conversations,
    isLoading: isConversationsLoading,
    refetch: refreshConversations,
  } = useQuery(
    // Same as the messages query below: the flag decides how the response is mapped, not which
    // conversations are requested, so it is not part of the identity.
    ['conversations', householdMembers],
    () => fetchConversations({ householdMembers, leslieEnabled }),
    {
      enabled: !!householdMembers,
      onSuccess: (conversations) => {
        if (!selectedConversationId)
          setSelectedConversationId(conversations[0].id);
      },
      onError: () => {
        toastr.error(
          intl.formatMessage(intlMessages.errorHeader),
          intl.formatMessage(intlMessages.failedToLoadConversationsErrorMsg),
        );
      },
    },
  );

  return {
    conversations,
    isLoading: isConversationsLoading,
    refreshConversations,
  };
}

function useMessages({ selectedConversation, intl, leslieEnabled }) {
  const { twoWayTextingDisplayFortressUsername, twoWayTextingAcceptImages } =
    useAppFlags();
  const selectedProperty = useSelectedProperty();
  const {
    data,
    isFetching,
    isFetchingPreviousPage,
    fetchPreviousPage: loadMoreMessages,
    refetch: refreshMessages,
    hasPreviousPage,
  } = useInfiniteQuery({
    enabled: !isNil(selectedConversation),
    initialData: { pages: [] },
    // Keyed on the ids, not the conversation and property objects. React Query hashes keys
    // structurally, so keying on the whole conversation made every field part of the identity --
    // including its status. Marking the conversation resolved therefore produced a brand new key
    // with no cached pages, so `messages` went momentarily empty and the surrounding UI (the
    // resolve banner among it) unmounted and remounted: a visible flicker on each toggle. Which
    // messages to load depends only on which conversation and property, so that is the key.
    // The flags below are deliberately not part of it: none of them changes the request, only how
    // the response is mapped, so keying on them would split the cache and bring the flicker back.
    queryKey: ['messages', selectedConversation?.id, selectedProperty?.id],
    queryFn: ({ pageParam }) =>
      fetchMessages({
        conversation: selectedConversation,
        selectedProperty,
        cursor: pageParam,
        includeUser: twoWayTextingDisplayFortressUsername,
        includeAttachments: twoWayTextingAcceptImages,
        leslieEnabled,
      }),
    getPreviousPageParam: (oldestPage, pages) => {
      const oldestMessage = oldestPage?.[0];
      return oldestMessage ? `before__${oldestMessage.id}` : null;
    },
    refetchInterval: 60000,
  });

  return {
    messages: data.pages.flat(),
    isLoading: isFetching,
    isLoadingMore: isFetchingPreviousPage,
    refreshMessages,
    loadMoreMessages,
    hasMoreMessages: hasPreviousPage,
  };
}

function useCommunicationPreferences({ contactId, intl }) {
  const selectedProperty = useSelectedProperty();

  const {
    isLoading,
    data: communicationPreferences,
    refetch: refreshCommunicationPreferences,
  } = useQuery(
    ['communicationPreferences', contactId],
    () => fetchCommunicationPreferences({ contactId, selectedProperty }),
    {
      enabled: !isNil(contactId),
      onError: (error) => {
        toastr.error(
          intl.formatMessage(intlMessages.errorHeader),
          intl.formatMessage(
            intlMessages.failedToLoadCommunicationPreferencesErrorMsg,
          ),
        );
      },
    },
  );

  const infoOptedIn = useMemo(() => {
    if (!communicationPreferences) return false;
    const infoPreference = communicationPreferences.find(
      (preference) => preference.communicationChannel.type === 'info',
    );
    return infoPreference?.isOptedIn || false;
  }, [communicationPreferences]);

  return {
    infoOptedIn,
    isLoading,
    refreshCommunicationPreferences,
  };
}
